import { Button } from '@chakra-ui/react';
import { DeleteIcon } from '@resistapp/client/components/icons/delete-icon';
import { MultiOptionConfirmModal } from '@resistapp/client/components/modals/multi-option-confirmation-modal';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import type { OrganisationMemberUser } from '@resistapp/common/api-types';
import { Feature, LabFeature } from '@resistapp/common/features';
import { AccessObjectType, Organisation } from '@resistapp/common/types';
import { useState } from 'react';
import {
  useDeleteOrganisation,
  useDeleteOrganisationInvite,
  useDeleteOrganisationProject,
  useDeleteOrganisationUser,
  usePatchOrganisation,
} from '../../hooks/api';

interface DeleteOrgUserIconProps {
  refreshMembers: () => Promise<void>;
  user: OrganisationMemberUser;
  organisation: Organisation;
}

export function DeleteOrgUserIcon(props: DeleteOrgUserIconProps) {
  const { refreshMembers, organisation, user } = props;
  const del = useDeleteOrganisationUser(organisation.id, user.id);
  return (
    <DeleteIcon
      triggerDelete={async () => {
        await del.mutateAsync();
        await refreshMembers();
      }}
      message={`${user.email} will be removed from ${organisation.name} organisation.\n\nAre you sure?`}
    />
  );
}

interface DeleteOrgInviteIconProps {
  refreshMembers: () => Promise<void>;
  inviteId: number;
  inviteEmail: string;
  organisation: Organisation;
}

export function DeleteOrgInviteIcon(props: DeleteOrgInviteIconProps) {
  const { refreshMembers, organisation, inviteId, inviteEmail } = props;
  const del = useDeleteOrganisationInvite(organisation.id, inviteId);
  return (
    <DeleteIcon
      triggerDelete={async () => {
        await del.mutateAsync();
        await refreshMembers();
      }}
      message={`Revoke invitation for ${inviteEmail} in ${organisation.name}?\n\nThe invite link will stop working immediately.`}
    />
  );
}

interface DeleteOrgAccessIconProps {
  organisation: Organisation;
  objectType: AccessObjectType;
  objectId: number;
  objectLabel?: string;
}

function DeleteOrgProjectAccessIcon({ organisation, objectId, objectLabel }: DeleteOrgAccessIconProps) {
  const deleteAccess = useDeleteOrganisationProject(organisation.id, objectId);
  const showToast = useToastWithoutDebounce();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const perform = async (strategy: 'keep' | 'revoke') => {
    const response = await (strategy === 'keep' ? deleteAccess.keep() : deleteAccess.revoke());
    const isSuccess = response.status !== undefined && response.status >= 200 && response.status <= 204;
    if (!isSuccess) {
      const serverMessage = (response.data as unknown as { message?: string } | undefined)?.message;
      const message = serverMessage || response.statusText || 'Failed to update access';
      showToast({ title: 'Failed to update access', description: message, status: 'error' });
      throw new Error(message);
    }
    window.location.reload();
  };

  return (
    <>
      <Button
        size="xs"
        variant="ghost"
        colorScheme="red"
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        Remove
      </Button>
      <MultiOptionConfirmModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        title={`Remove ${organisation.name}'s access`}
        message={`Choose how ${organisation.name} should lose access to ${objectLabel || 'this project'}.`}
        options={[
          {
            key: 'keep',
            label: 'Remove project access only',
            variant: 'outline',
            colorScheme: 'blue',
            description: 'Keep sampling point and sample permissions unchanged.',
            onSelect: async () => {
              await perform('keep');
            },
          },
          {
            key: 'revoke',
            label: 'Remove project and sampling access',
            colorScheme: 'red',
            description: 'Revoke sampling point and sample permissions granted via this project.',
            onSelect: async () => {
              await perform('revoke');
            },
          },
        ]}
      />
    </>
  );
}

function DeleteOrgDirectAccessIcon({ organisation, objectType, objectId, objectLabel }: DeleteOrgAccessIconProps) {
  if (objectType === AccessObjectType.PROJECT) {
    throw new Error('DeleteOrgDirectAccessIcon should not receive PROJECT objectType');
  }
  const patch = usePatchOrganisation();
  const typeLabel = objectType === AccessObjectType.SAMPLING_POINT ? 'sampling point' : 'sample';
  const resolvedLabel =
    objectLabel ??
    (objectType === AccessObjectType.SAMPLING_POINT ? `Sampling point #${objectId}` : `Sample #${objectId}`);
  const payload = {
    projectAccesses: organisation.projectAccesses,
    samplingPointAccesses:
      objectType === AccessObjectType.SAMPLING_POINT
        ? organisation.samplingPointAccesses.filter(id => id !== objectId)
        : organisation.samplingPointAccesses,
    sampleAccesses:
      objectType === AccessObjectType.SAMPLE
        ? organisation.sampleAccesses.filter(id => id !== objectId)
        : organisation.sampleAccesses,
  };
  return (
    <DeleteIcon
      triggerDelete={async () => {
        await patch.mutateAsync({
          id: organisation.id,
          ...payload,
        });
        window.location.reload();
      }}
      message={`${organisation.name} will lose access to ${typeLabel} '${resolvedLabel}' (${objectId}).\n\nAre you sure?`}
    />
  );
}

export function DeleteOrgAccessIcon(props: DeleteOrgAccessIconProps) {
  if (props.objectType === AccessObjectType.PROJECT) {
    return <DeleteOrgProjectAccessIcon {...props} />;
  }
  return <DeleteOrgDirectAccessIcon {...props} />;
}

export function DeleteFeatureLink(props: { featureToDelete: Feature; organisation: Organisation }) {
  const { organisation, featureToDelete } = props;
  const del = usePatchOrganisation();
  return (
    <DeleteIcon
      triggerDelete={async () => {
        await del.mutateAsync({
          id: organisation.id,
          features: organisation.features.filter(f => f !== featureToDelete),
        });
      }}
      message={`Remove ${organisation.name}'s access to ${featureToDelete}?`}
    />
  );
}

export function DeleteLabFeatureLink(props: { featureToDelete: LabFeature; organisation: Organisation }) {
  const { organisation, featureToDelete } = props;
  const patch = usePatchOrganisation();
  return (
    <DeleteIcon
      triggerDelete={async () => {
        await patch.mutateAsync({
          id: organisation.id,
          labFeatures: organisation.labFeatures.filter(feature => feature !== featureToDelete),
        });
      }}
      message={`Remove ${organisation.name}'s access to the ${featureToDelete.replace(/_/g, ' ').toLowerCase()} step?`}
    />
  );
}

export function DeleteOrganisationLink(props: { organisation: Organisation }) {
  const { organisation } = props;
  const del = useDeleteOrganisation(organisation.id);
  return (
    <DeleteIcon
      triggerDelete={async () => {
        await del.mutateAsync();
      }}
      message={`Delete ${organisation.name}?\n\nDeletion only succeeds if the organisation has no users, child organisations, accesses, or lab workbooks.`}
    />
  );
}
